import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useContext,
  useCallback,
} from "react";
import styles from "./Library.module.css";
import { db } from "../../services/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { UserContext } from "../../context/UserContext";
import { setDoc } from "firebase/firestore";
import ActivityModal from "./ActivityModal";
import { DataContext } from "../../context/DataContext";

const axesMeta = [
  { key: "mind", label: "Mind" },
  { key: "move", label: "Move" },
  { key: "nutri", label: "Nutri" },
  { key: "values", label: "Values" },
];

export default function LibraryPage() {
  const { user } = useContext(UserContext);
  const { loadingData, activitiesList } = useContext(DataContext);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inputRef = useRef(null);
  const uid = user?.uid;

  // Debounce the query (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Key handlers for accessibility: ESC clears, / focuses
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setQuery("");
        inputRef.current?.blur();
      }
      if (e.key === "/") {
        const el = document.activeElement;
        if (el && el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const [axisFilter, setAxisFilter] = useState("all");
  const [addingMap, setAddingMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalActivity, setModalActivity] = useState(null);

  // Pagination / infinite scroll state
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const loaderRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);

  // Flatten activitiesList (attach axis)
  const flattened = useMemo(() => {
    if (!activitiesList || typeof activitiesList !== "object") return [];
    return Object.entries(activitiesList).flatMap(([axis, list]) =>
      Array.isArray(list) ? list.map((it) => ({ ...it, axis })) : []
    );
  }, [activitiesList]);

  // axis filter
  const axisFiltered = useMemo(() => {
    if (axisFilter === "all") return flattened;
    return flattened.filter((i) => i.axis === axisFilter);
  }, [flattened, axisFilter]);

  // normalized query
  const normalizedQuery = debouncedQuery.toLowerCase();

  // full filtered array (by query)
  const filtered = useMemo(() => {
    if (!normalizedQuery) return axisFiltered;
    return axisFiltered.filter((it) => {
      const title = (it.title || "").toLowerCase();
      if (title.includes(normalizedQuery)) return true;

      const desc = (it.description || it.content || "").toLowerCase();
      if (desc.includes(normalizedQuery)) return true;

      if (Array.isArray(it.tags)) {
        for (const t of it.tags) {
          if ((t || "").toLowerCase().includes(normalizedQuery)) return true;
        }
      }
      return false;
    });
  }, [axisFiltered, normalizedQuery]);

  // visible items slice for current page
  const visibleItems = useMemo(() => {
    return filtered.slice(0, page * PAGE_SIZE);
  }, [filtered, page]);

  // update hasMore whenever filtered or visibleItems change
  useEffect(() => {
    setHasMore(visibleItems.length < filtered.length);
  }, [visibleItems.length, filtered.length]);

  // reset page when filter/query changes
  useEffect(() => {
    setPage(1);
    // scroll to top of library area (optional)
    // document.querySelector(`.${styles.page}`)?.scrollIntoView({ behavior: "smooth" });
  }, [axisFilter, debouncedQuery]);

  // intersection observer to load more
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMore) {
            setPage((p) => p + 1);
          }
        });
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      observer.disconnect();
    };
  }, [hasMore]);

  function youtubeIdFromUrl(url = "") {
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) {
        return u.pathname.slice(1);
      }
      if (u.searchParams && u.searchParams.get("v"))
        return u.searchParams.get("v");
    } catch (e) {}
    const m = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
    return m ? m[1] : null;
  }

  // add to plan (kept from your code)
  async function addToPlanInline(item, axisChoice, dayChoice) {
    if (!uid) {
      alert("Faça login para adicionar atividades ao plano.");
      return;
    }
    const key = `${item.id || item.title}-${axisChoice}-${dayChoice}`;
    setAddingMap((s) => ({ ...s, [key]: true }));
    try {
      const userRef = doc(db, "users", uid);
      const snap = await getDoc(userRef);
      const userDoc = snap.exists() ? snap.data() : {};
      const currentPlans = userDoc.plans || {};

      if (!currentPlans[axisChoice]) {
        currentPlans[axisChoice] = {
          sunday: [],
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          repeatWeekly: false,
        };
      }
      const itemObj = {
        id: item.id || item.title || Math.random().toString(36).slice(2, 9),
        title: item.title || item.content || item,
        axis: axisChoice,
        dateAddedAt: new Date().toISOString(),
        userCreated: false,
        url: item.url || null,
      };
      currentPlans[axisChoice][dayChoice] =
        currentPlans[axisChoice][dayChoice] || [];
      currentPlans[axisChoice][dayChoice].push(itemObj);

      await setDoc(
        userRef,
        { plans: currentPlans, plansUpdatedAt: new Date() },
        { merge: true }
      );
    } catch (err) {
      console.error("Erro ao adicionar ao plano:", err);
      alert("Erro ao adicionar ao plano. Tente novamente.");
    } finally {
      setAddingMap((s) => ({ ...s, [key]: false }));
    }
  }

  // manual load more fallback
  const loadMore = useCallback(() => {
    if (!hasMore) return;
    setPage((p) => p + 1);
  }, [hasMore]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Biblioteca de Atividades</h1>
      </header>

      <div className={styles.searchRow}>
        <label htmlFor="librarySearch" className={styles.srOnly}>
          Pesquisar na biblioteca
        </label>
        <input
          id="librarySearch"
          ref={inputRef}
          className={styles.searchInput}
          placeholder="Pesquisar por título, descrição ou tag"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Pesquisar na biblioteca"
        />
        <button
          className={styles.clearBtn}
          onClick={() => setQuery("")}
          aria-label="Limpar pesquisa"
          title="Limpar"
        >
          ✕
        </button>
      </div>

      <div className={styles.controls}>
        <div>
          <label className={styles.label}>Filtrar eixo</label>
          <select
            value={axisFilter}
            onChange={(e) => setAxisFilter(e.target.value)}
            className={styles.smallSelect}
          >
            <option value="all">Todos</option>
            {axesMeta.map((a) => (
              <option value={a.key} key={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <main className={styles.grid}>
        {loadingData ? (
          <p>Carregando atividades…</p>
        ) : visibleItems.length === 0 ? (
          <p className={styles.empty}>Nenhuma atividade encontrada.</p>
        ) : (
          visibleItems.map((it, idx) => {
            const yid = youtubeIdFromUrl(it.url || "");
            const thumbnail = yid
              ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg`
              : null;
            const defaultAxis = it.axis || "mind";

            return (
              <article
                className={styles.card}
                key={it.id || `${it.title}-${idx}`}
              >
                <div className={styles.cardTop}>
                  {thumbnail ? (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.thumbLink}
                    >
                      <img
                        src={thumbnail}
                        alt={it.title || "Vídeo"}
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <div
                      className={styles.placeholder}
                      onClick={() => {
                        setModalActivity(it);
                        setModalOpen(true);
                      }}
                    >
                      <div className={styles.placeholderText}>
                        {it.title || it.content || "Atividade"}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.cardBody}>
                  <h3 className={styles.title}>
                    {it.title || it.content || "Atividade"}
                  </h3>
                  <p className={styles.axisTag}>
                    {it.axis?.toUpperCase() || defaultAxis.toUpperCase()}
                  </p>
                </div>
              </article>
            );
          })
        )}
      </main>

      {/* loader element observed by IntersectionObserver */}
      <div ref={loaderRef} style={{ height: 1 }} />

      {/* manual fallback load more button */}
      <div style={{ textAlign: "center", marginTop: 18 }}>
        {hasMore ? (
          <button className="defaultButton" onClick={loadMore}>
            Carregar mais
          </button>
        ) : (
          <div className={styles.empty}>Fim da lista</div>
        )}
      </div>

      <ActivityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        activity={modalActivity}
        onAddToPlan={addToPlanInline}
      />
    </div>
  );
}
